import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  type DragOverEvent,
  type DragStartEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import type {
  ProfileModuleDto,
  ProfileModuleType,
} from '@up2stay/up2promo-api-client';
import type { ProfileTemplateFieldMode } from '@up2stay/up2promo-api-client';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@up2stay/up2promo-ui';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@up2stay/up2promo-ui';
import { ConfirmDialog } from '@up2stay/up2promo-ui';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@up2stay/up2promo-ui';
import { cn, Input, Switch } from '@up2stay/up2promo-ui';
import {
  Check,
  ChevronDown,
  GripVertical,
  MoreVertical,
  Pencil,
  Plus,
  RotateCcw,
  Trash2,
} from 'lucide-react';
import { useRef, useState } from 'react';

import { TemplateCustomizationHint } from '@/core/components/profile-templates/template-customization-hint';
import type { TemplateMemberValuesEditorUiProps } from '@/core/components/profile-templates/template-policy-member-controls';
import { useDndProfileSensors } from '@/core/hooks/use-dnd-profile-sensors';
import { useIsMobile } from '@/core/hooks/use-mobile';
import {
  lockBodyScrollDuringDrag,
  triggerDragEndHaptic,
  triggerDragOverHaptic,
  triggerDragStartHaptic,
  unlockBodyScrollAfterDrag,
} from '@/core/lib/dnd-haptics';
import {
  getModuleLabel,
  getModuleSectionTitle,
  MODULE_TYPE_LABELS,
} from '@/core/lib/module-labels';
import {
  getModuleId,
  getTemplateModuleOverrideDisplay,
  isPerEntryListModuleType,
  type TemplateEntryFieldLabels,
} from '@/core/lib/profile-module-entry-policies';
import { useDictionary } from '@/core/providers/dictionary-provider';

import { ModuleConfigWrapper } from './module-config-wrapper';
import type { ProfileModulesSectionProps } from '@/core/components/profile/profile-modules-section-types';

export interface SortableModuleItemProps {
  module: ProfileModuleDto;
  index: number;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onToggle: () => void;
  onConfigChange: (config: Record<string, unknown>) => void;
  onSectionTitleChange: (value: string | undefined) => void;
  onRemove: () => void;
  dict: {
    reorder: string;
    sectionTitleLabel: string;
    resetToDefault: string;
    reset: string;
    confirm: string;
    collapse: string;
    expand: string;
    editSectionTitle: string;
    /** Name of the mobile ⋮ menu, „{title}“ = module (DEV-540). */
    moduleOptions: string;
    moduleEnable: string;
    moduleDisable: string;
    moduleVisible?: string;
    removeModule: string;
    removeConfirmTitle?: string;
    removeConfirmDescription?: string;
    removeConfirmAction?: string;
    removeConfirmCancel?: string;
  };
  readOnly?: boolean;
  structureReadOnly?: boolean;
  templateModules?: ProfileModuleDto[];
  showEntryPolicies?: boolean;
  onModuleEntryPolicyChange?: (
    moduleId: string,
    entryId: string,
    mode: ProfileTemplateFieldMode,
  ) => void;
  policyLabels?: { locked: string; free: string };
  fieldLockedHintMessage?: string;
  isModuleCustomized?: (moduleId: string) => boolean;
  onResetModuleToTemplate?: (moduleId: string) => void;
  templateOverrideLabels?: ProfileModulesSectionProps['templateOverrideLabels'];
  overrideHintMessage?: string;
  showEntryOverrideHints?: boolean;
  templateTeamIds?: string[];
  showManageTeamLink?: boolean;
  workspaceId?: ProfileModulesSectionProps['workspaceId'];
  templateId?: ProfileModulesSectionProps['templateId'];
  publishStatus?: ProfileModulesSectionProps['publishStatus'];
  memberRoster?: ProfileModulesSectionProps['memberRoster'];
  memberRosterLoading?: ProfileModulesSectionProps['memberRosterLoading'];
  hasAssignedTeams?: ProfileModulesSectionProps['hasAssignedTeams'];
  memberValuesDict?: ProfileModulesSectionProps['memberValuesDict'];
}

export function SortableModuleItem({
  module,
  index,
  isOpen,
  onOpenChange,
  onToggle,
  onConfigChange,
  onSectionTitleChange,
  onRemove,
  dict,
  readOnly = false,
  structureReadOnly = false,
  templateModules,
  showEntryPolicies = false,
  onModuleEntryPolicyChange,
  policyLabels,
  fieldLockedHintMessage,
  isModuleCustomized,
  onResetModuleToTemplate,
  templateOverrideLabels,
  overrideHintMessage,
  showEntryOverrideHints,
  templateTeamIds,
  showManageTeamLink,
  workspaceId,
  templateId,
  publishStatus,
  memberRoster,
  memberRosterLoading,
  hasAssignedTeams,
  memberValuesDict,
}: SortableModuleItemProps) {
  const blockStructure = readOnly || structureReadOnly;
  const moduleId = getModuleId(module);
  const isMobile = useIsMobile();
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [editingValue, setEditingValue] = useState('');
  const [menuOpen, setMenuOpen] = useState(false);
  const [confirmRemoveOpen, setConfirmRemoveOpen] = useState(false);

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: String(index), disabled: blockStructure });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition: isDragging ? transition : 'none',
  };

  const defaultLabel = getModuleLabel(module.type);
  const displayTitle = getModuleSectionTitle(module, defaultLabel);
  const sectionTitleValue = (module.config?.sectionTitle as string) ?? '';

  const templateModule = templateModules?.find(
    (candidate) => getModuleId(candidate) === moduleId,
  );
  const moduleTemplateDisplay =
    templateModule && templateOverrideLabels && isModuleCustomized?.(moduleId)
      ? getTemplateModuleOverrideDisplay(module, templateModule, {
          ...templateOverrideLabels.entryFields,
          emptyValue: templateOverrideLabels.emptyValue,
          moduleNoContent: templateOverrideLabels.moduleNoContent,
          moduleEntries: templateOverrideLabels.moduleEntries,
        })
      : null;

  const startEditing = () => {
    setEditingValue(sectionTitleValue);
    setIsEditingTitle(true);
  };

  const saveTitle = () => {
    const value = editingValue.trim();
    onSectionTitleChange(value || undefined);
    setIsEditingTitle(false);
  };

  const handleResetTitle = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setEditingValue('');
    onSectionTitleChange('');
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        'border rounded-lg',
        isDragging && 'opacity-50 shadow-md z-10 ring-2 ring-primary',
        isMobile && 'touch-pan-y cursor-grab active:cursor-grabbing',
      )}
    >
      <Collapsible open={isOpen} onOpenChange={onOpenChange}>
        <div
          className="group flex min-h-10 items-center gap-3 p-3 min-w-0"
          data-editing-title={isEditingTitle ? 'true' : undefined}
          {...(isMobile ? { ...listeners, ...attributes } : {})}
        >
          {!blockStructure ? (
            <div
              data-dnd-grip
              {...(!isMobile ? { ...listeners, ...attributes } : {})}
              className={cn(
                'touch-none shrink-0 rounded p-0.5 hover:bg-accent cursor-grab active:cursor-grabbing',
              )}
              aria-label={dict.reorder}
            >
              <GripVertical className="h-5 w-5 text-muted-foreground" />
            </div>
          ) : null}
          <CollapsibleTrigger asChild>
            <button
              type="button"
              className="max-sm:group-data-[editing-title=true]:hidden shrink-0 rounded p-1"
              aria-label={`${isOpen ? dict.collapse : dict.expand}: ${displayTitle}`}
            >
              <ChevronDown
                className={cn(
                  'h-4 w-4 text-muted-foreground transition-transform',
                  isOpen && 'rotate-180',
                )}
              />
            </button>
          </CollapsibleTrigger>
          {/* Title area: fixed height so card doesn't jump when switching to edit */}
          <div className="flex min-h-8 flex-1 items-center gap-2 min-w-0">
            {isEditingTitle ? (
              <div
                className="flex min-h-8 flex-1 items-center gap-2 min-w-0"
                onClick={(e) => e.stopPropagation()}
              >
                <Input
                  value={editingValue}
                  onChange={(e) => setEditingValue(e.target.value)}
                  placeholder={defaultLabel}
                  className="h-8 font-medium flex-1 min-w-0 shrink-0"
                  onBlur={saveTitle}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      saveTitle();
                      e.currentTarget.blur();
                    }
                    if (e.key === 'Escape') {
                      setIsEditingTitle(false);
                    }
                  }}
                  autoFocus
                  aria-label={dict.sectionTitleLabel}
                />
                <button
                  type="button"
                  disabled={!editingValue.trim()}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={handleResetTitle}
                  className="shrink-0 flex cursor-pointer items-center gap-1.5 rounded-md px-2 py-1.5 text-xs text-muted-foreground hover:bg-accent hover:text-foreground disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-transparent disabled:hover:text-muted-foreground"
                  aria-label={dict.resetToDefault}
                >
                  <RotateCcw className="h-3.5 w-3.5 shrink-0" />
                  <span className="max-sm:hidden">{dict.reset}</span>
                </button>
                <button
                  type="button"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={saveTitle}
                  className="shrink-0 flex h-8 w-8 cursor-pointer items-center justify-center rounded-md text-primary hover:bg-accent"
                  aria-label={dict.confirm}
                >
                  <Check className="h-5 w-5" />
                </button>
              </div>
            ) : (
              <>
                <CollapsibleTrigger asChild>
                  <button
                    type="button"
                    className="flex h-8 min-h-8 flex-1 min-w-0 cursor-pointer items-center text-left font-medium truncate"
                  >
                    <span className="truncate">{displayTitle}</span>
                  </button>
                </CollapsibleTrigger>
                {!isMobile && !blockStructure && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      startEditing();
                    }}
                    className="flex h-8 w-8 shrink-0 cursor-pointer items-center justify-center rounded p-1 text-muted-foreground hover:bg-accent hover:text-foreground"
                    aria-label={dict.editSectionTitle}
                  >
                    <Pencil className="h-3.5 w-3.5" />
                  </button>
                )}
              </>
            )}
          </div>
          {isMobile && !blockStructure ? (
            <DropdownMenu
              open={menuOpen}
              onOpenChange={setMenuOpen}
              modal={false}
            >
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  onPointerDown={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    setMenuOpen(true);
                  }}
                  className="flex h-8 w-8 shrink-0 cursor-pointer items-center justify-center rounded p-1 text-muted-foreground hover:bg-accent hover:text-foreground max-sm:group-data-[editing-title=true]:hidden"
                  aria-label={dict.moduleOptions.replace(
                    '{title}',
                    displayTitle,
                  )}
                >
                  <MoreVertical className="h-5 w-5" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="end"
                onClick={(e) => e.stopPropagation()}
              >
                <DropdownMenuItem onSelect={() => startEditing()}>
                  <Pencil className="h-4 w-4" />
                  {dict.editSectionTitle}
                </DropdownMenuItem>
                <DropdownMenuItem
                  onSelect={(event) => {
                    event.preventDefault();
                    setMenuOpen(false);
                    setConfirmRemoveOpen(true);
                  }}
                  className="text-destructive focus:text-destructive"
                >
                  <Trash2 className="h-4 w-4" />
                  {dict.removeModule}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : !blockStructure ? (
            <>
              <span className="max-sm:group-data-[editing-title=true]:hidden">
                <Switch
                  checked={module.enabled}
                  onCheckedChange={onToggle}
                  onClick={(e) => e.stopPropagation()}
                  aria-label={
                    module.enabled
                      ? dict.moduleDisable.replace(
                          '{title}',
                          getModuleSectionTitle(
                            module,
                            getModuleLabel(module.type),
                          ),
                        )
                      : dict.moduleEnable.replace(
                          '{title}',
                          getModuleSectionTitle(
                            module,
                            getModuleLabel(module.type),
                          ),
                        )
                  }
                />
              </span>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setConfirmRemoveOpen(true);
                }}
                className="max-sm:group-data-[editing-title=true]:hidden cursor-pointer p-1 text-destructive hover:bg-destructive/10 rounded transition-colors shrink-0"
                aria-label={dict.removeModule}
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </>
          ) : null}
        </div>
        <CollapsibleContent>
          <div className="border-t px-3 py-3 md:p-4">
            {isMobile && !blockStructure && (
              <div className="flex items-center justify-between gap-3 pb-3 mb-3 border-b border-border">
                <span className="text-sm font-medium">
                  {dict.moduleVisible ?? 'Modul anzeigen'}
                </span>
                <Switch
                  checked={module.enabled}
                  onCheckedChange={onToggle}
                  onClick={(e) => e.stopPropagation()}
                  aria-label={
                    module.enabled
                      ? dict.moduleDisable.replace(
                          '{title}',
                          getModuleSectionTitle(
                            module,
                            getModuleLabel(module.type),
                          ),
                        )
                      : dict.moduleEnable.replace(
                          '{title}',
                          getModuleSectionTitle(
                            module,
                            getModuleLabel(module.type),
                          ),
                        )
                  }
                />
              </div>
            )}
            <ModuleConfigWrapper
              variant="inline"
              module={module}
              readOnly={readOnly}
              onConfigChange={onConfigChange}
              structureReadOnly={structureReadOnly}
              templateModules={templateModules}
              moduleId={moduleId}
              showEntryPolicies={showEntryPolicies}
              onEntryPolicyChange={
                onModuleEntryPolicyChange
                  ? (entryId, mode) =>
                      onModuleEntryPolicyChange(moduleId, entryId, mode)
                  : undefined
              }
              policyLabels={policyLabels}
              fieldLockedHintMessage={fieldLockedHintMessage}
              showEntryOverrideHints={showEntryOverrideHints}
              templateOverrideLabels={
                templateOverrideLabels
                  ? {
                      hint: templateOverrideLabels.hint,
                      reset: templateOverrideLabels.reset,
                      templateValue: templateOverrideLabels.templateValue,
                      entryFields: templateOverrideLabels.entryFields,
                    }
                  : undefined
              }
              overrideHintMessage={overrideHintMessage}
              templateTeamIds={templateTeamIds}
              showManageTeamLink={showManageTeamLink}
              workspaceId={workspaceId}
              templateId={templateId}
              publishStatus={publishStatus}
              memberRoster={memberRoster}
              memberRosterLoading={memberRosterLoading}
              hasAssignedTeams={hasAssignedTeams}
              memberValuesDict={memberValuesDict}
            />
            {templateOverrideLabels &&
            onResetModuleToTemplate &&
            !isPerEntryListModuleType(module.type) &&
            moduleTemplateDisplay ? (
              <TemplateCustomizationHint
                customized
                message={templateOverrideLabels.hint}
                resetLabel={templateOverrideLabels.reset}
                templateValueLabel={templateOverrideLabels.templateValue}
                templateValue={moduleTemplateDisplay.single}
                templateDetails={moduleTemplateDisplay.details}
                onReset={() => onResetModuleToTemplate(moduleId)}
                className="mt-1.5"
              />
            ) : null}
          </div>
        </CollapsibleContent>
      </Collapsible>

      <ConfirmDialog
        open={confirmRemoveOpen}
        onOpenChange={setConfirmRemoveOpen}
        title={(
          dict.removeConfirmTitle ?? 'Modul „{title}“ entfernen?'
        ).replace('{title}', displayTitle)}
        description={
          dict.removeConfirmDescription ??
          'Alle Inhalte dieses Moduls werden gelöscht.'
        }
        confirmLabel={dict.removeConfirmAction ?? 'Entfernen'}
        cancelLabel={dict.removeConfirmCancel ?? 'Abbrechen'}
        destructive
        onConfirm={() => {
          setConfirmRemoveOpen(false);
          onRemove();
        }}
      />
    </div>
  );
}
